/**
 * Type-safe API client with CSRF token management
 */

import type { Block } from './BlockEditor.js';
import { Auth } from './Auth.js';

export interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
}

export interface User {
    id: number;
    email: string;
    created_at: number; // Unix timestamp
}

export interface Session {
    id: number;
    ip_address: string | null;
    user_agent: string | null;
    device_name: string | null;
    created_at: number;
    last_active_at: number;
    expires_at: number;
    is_current: boolean;
}

export interface LoginHistoryEntry {
    created_at: number;
    ip_address: string | null;
    user_agent: string | null;
    metadata: string | null;
    event_type: string;
    result: string;
}

export interface MagicLinkRequestResponse {
    message: string;
    request_id: string;
}

export interface MagicLinkStatusResponse {
    pending: boolean;
    cross_device_detected: boolean;
    expired: boolean;
    verified?: boolean;
    invalidated?: boolean;
    access_token?: string;
}

export interface AuthMethodResponse {
    auth_method: 'magic_link' | 'webauthn';
    recovery_available: boolean;
}

export interface WebAuthnCredential {
    id: number;
    name: string;
    last_used_at: number | null;
    created_at: number;
}

export interface RecoveryRequest {
    id: number;
    user_id: number;
    email: string;
    status: string;
    ip_address: string;
    user_agent: string | null;
    ip_user_agent_match: boolean;
    requested_at: number;
    expires_at: number;
}

export interface EmailTemplateUpdate {
    subject: string;
    preheader: string;
    blocks: Block[];
}

export class ApiError extends Error {
    constructor(
        message: string,
        public readonly statusCode: number,
        public readonly response?: ApiResponse
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export class ApiClient {
    private csrfToken: string | null = null;
    private readonly baseUrl: string;

    constructor(baseUrl: string = '') {
        this.baseUrl = baseUrl;
    }

    /**
     * Fetch CSRF token from the server
     */
    async getCsrfToken(): Promise<string> {
        if (this.csrfToken !== null) {
            return this.csrfToken;
        }

        const response = await fetch(`${this.baseUrl}/api/auth/csrf-token`, {
            method: 'GET',
            credentials: 'include',
        });

        if (!response.ok) {
            throw new ApiError('Failed to fetch CSRF token', response.status);
        }

        const data = await response.json() as ApiResponse<{ csrf_token: string }>;

        if (!data.success || !data.data?.csrf_token) {
            throw new ApiError('Invalid CSRF token response', response.status, data);
        }

        this.csrfToken = data.data.csrf_token;
        return this.csrfToken;
    }

    /**
     * Make an API request
     * If the request fails with 401 and it's an authenticated request,
     * attempts to refresh the token and retry once.
     */
    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        isRetry: boolean = false
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${endpoint}`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
        });

        // Handle 401 for authenticated requests - try to refresh token
        if (response.status === 401 && !isRetry) {
            const authHeader = (options.headers as Record<string, string>)?.['Authorization'];
            if (authHeader?.startsWith('Bearer ')) {
                const newToken = await Auth.getValidAccessToken();
                if (newToken) {
                    // Retry with new token
                    const retryHeaders = {
                        ...options.headers,
                        'Authorization': `Bearer ${newToken}`,
                    };
                    return this.request<T>(endpoint, { ...options, headers: retryHeaders }, true);
                } else {
                    // Refresh failed - user is no longer authenticated
                    Auth.redirectToLogin();
                    throw new ApiError('Session expired', 401);
                }
            }
        }

        const data = await response.json() as ApiResponse<T>;

        if (!response.ok) {
            throw new ApiError(
                data.message || data.error || 'Request failed',
                response.status,
                data
            );
        }

        return data;
    }

    /**
     * Make a POST request with CSRF token
     */
    private async postWithCsrf<T>(
        endpoint: string,
        body: unknown
    ): Promise<ApiResponse<T>> {
        const csrfToken = await this.getCsrfToken();

        return this.request<T>(endpoint, {
            method: 'POST',
            headers: {
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(body),
        });
    }

    /**
     * Make an authenticated API request.
     * Automatically handles token refresh if the token is expired.
     * Use this for generic authenticated requests instead of raw fetch.
     */
    async authenticatedRequest<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        const token = await Auth.getValidAccessToken();
        if (!token) {
            Auth.redirectToLogin();
            throw new ApiError('Session expired', 401);
        }

        return this.request<T>(endpoint, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
            },
        });
    }

    /**
     * Make an authenticated fetch request that returns the raw Response.
     * Use this for file downloads or non-JSON responses.
     */
    async authenticatedFetch(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<Response> {
        const token = await Auth.getValidAccessToken();
        if (!token) {
            Auth.redirectToLogin();
            throw new ApiError('Session expired', 401);
        }

        return fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
            },
            credentials: 'include',
        });
    }

    /**
     * Make an authenticated POST request with CSRF token.
     */
    async authenticatedPost<T>(
        endpoint: string,
        body: unknown
    ): Promise<ApiResponse<T>> {
        const csrfToken = await this.getCsrfToken();

        return this.authenticatedRequest<T>(endpoint, {
            method: 'POST',
            headers: {
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(body),
        });
    }

    /**
     * Make an authenticated PUT request with CSRF token.
     */
    async authenticatedPut<T>(
        endpoint: string,
        body: unknown
    ): Promise<ApiResponse<T>> {
        const csrfToken = await this.getCsrfToken();

        return this.authenticatedRequest<T>(endpoint, {
            method: 'PUT',
            headers: {
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(body),
        });
    }

    /**
     * Make an authenticated DELETE request with CSRF token.
     */
    async authenticatedDelete<T = null>(
        endpoint: string
    ): Promise<ApiResponse<T>> {
        const csrfToken = await this.getCsrfToken();

        return this.authenticatedRequest<T>(endpoint, {
            method: 'DELETE',
            headers: {
                'X-CSRF-Token': csrfToken,
            },
        });
    }

    /**
     * Make an authenticated file upload request.
     * Does not set Content-Type header (browser sets it with boundary for FormData).
     */
    async authenticatedUpload<T>(
        endpoint: string,
        formData: FormData
    ): Promise<ApiResponse<T>> {
        const token = await Auth.getValidAccessToken();
        if (!token) {
            Auth.redirectToLogin();
            throw new ApiError('Session expired', 401);
        }

        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
            credentials: 'include',
        });

        const data = await response.json() as ApiResponse<T>;

        if (!response.ok) {
            throw new ApiError(
                data.message || data.error || 'Upload failed',
                response.status,
                data
            );
        }

        return data;
    }

    /**
     * Request a magic link
     */
    async requestMagicLink(email: string): Promise<MagicLinkRequestResponse> {
        const response = await this.postWithCsrf<MagicLinkRequestResponse>(
            '/api/auth/magic-link/request',
            { email }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to request magic link', 400, response);
        }

        return response.data;
    }

    /**
     * Check magic link verification status
     */
    async checkMagicLinkStatus(requestId: string): Promise<MagicLinkStatusResponse> {
        const response = await this.request<MagicLinkStatusResponse>(
            `/api/auth/magic-link/status?request_id=${encodeURIComponent(requestId)}`,
            { method: 'GET' }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to check magic link status', 400, response);
        }

        return response.data;
    }

    /**
     * Verify magic link token (for cross-device flow)
     */
    async verifyMagicLinkCode(email: string, code: string): Promise<{ access_token: string }> {
        const response = await this.postWithCsrf<{ access_token: string }>(
            '/api/auth/magic-link/verify-code',
            { email, code }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to verify magic link code', 400, response);
        }

        return response.data;
    }

    /**
     * Get current user info
     */
    async getCurrentUser(accessToken: string): Promise<User> {
        const response = await this.request<{ user: User }>(
            '/api/auth/me',
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.user) {
            throw new ApiError('Failed to get current user', 400, response);
        }

        return response.data.user;
    }

    /**
     * Logout user
     */
    async logout(): Promise<void> {
        await this.postWithCsrf('/api/auth/logout', {});
        this.csrfToken = null;
    }

    /**
     * Upgrade the current short-lived session to a persistent session
     */
    async persistSession(): Promise<{ access_token: string }> {
        const csrfToken = await this.getCsrfToken();

        const response = await this.request<{ access_token: string }>(
            '/api/auth/persist',
            {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': csrfToken,
                },
                credentials: 'include',
            }
        );

        if (!response.success || !response.data?.access_token) {
            throw new ApiError('Failed to persist session', 400, response);
        }

        return response.data;
    }

    /**
     * Get active sessions
     */
    async getSessions(accessToken: string): Promise<Session[]> {
        const response = await this.request<{ sessions: Session[] }>(
            '/api/sessions',
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.sessions) {
            throw new ApiError('Failed to get sessions', 400, response);
        }

        return response.data.sessions;
    }

    /**
     * Get login history
     */
    async getLoginHistory(accessToken: string, limit = 50, offset = 0): Promise<LoginHistoryEntry[]> {
        const response = await this.request<{ history: LoginHistoryEntry[] }>(
            `/api/sessions/history?limit=${limit}&offset=${offset}`,
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.history) {
            throw new ApiError('Failed to get login history', 400, response);
        }

        return response.data.history;
    }

    /**
     * Revoke a session
     */
    async revokeSession(accessToken: string, sessionId: number): Promise<void> {
        const csrfToken = await this.getCsrfToken();

        await this.request(
            `/api/sessions/revoke?id=${sessionId}`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
            }
        );
    }

    /**
     * Revoke all sessions except current
     */
    async revokeAllSessions(accessToken: string): Promise<{ revoked_count: number; access_token: string }> {
        const csrfToken = await this.getCsrfToken();

        const response = await this.request<{ revoked_count: number; access_token: string }>(
            '/api/sessions/revoke-all',
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
            }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to revoke sessions', 400, response);
        }

        return response.data;
    }

    /**
     * Check which authentication method to use for an email
     */
    async checkAuthMethod(email: string): Promise<AuthMethodResponse> {
        const response = await this.request<AuthMethodResponse>(
            '/api/auth/check-auth-method',
            {
                method: 'POST',
                body: JSON.stringify({ email }),
            }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to check auth method', 400, response);
        }

        return response.data;
    }

    /**
     * Get WebAuthn registration options
     */
    async getWebAuthnRegistrationOptions(accessToken: string): Promise<unknown> {
        const response = await this.request<{ options: unknown }>(
            '/api/auth/webauthn/register/options',
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.options) {
            throw new ApiError('Failed to get registration options', 400, response);
        }

        return response.data.options;
    }

    /**
     * Register WebAuthn credential
     */
    async registerWebAuthnCredential(
        accessToken: string,
        name: string,
        credentialResponse: unknown
    ): Promise<{ credential_id: number }> {
        const response = await this.request<{ credential_id: number }>(
            '/api/auth/webauthn/register',
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    name,
                    response: credentialResponse,
                }),
            }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to register credential', 400, response);
        }

        return response.data;
    }

    /**
     * Get WebAuthn authentication options
     */
    async getWebAuthnAuthenticationOptions(email: string): Promise<unknown> {
        const response = await this.request<{ options: unknown }>(
            '/api/auth/webauthn/authenticate/options',
            {
                method: 'POST',
                body: JSON.stringify({ email }),
            }
        );

        if (!response.success || !response.data?.options) {
            throw new ApiError('Failed to get authentication options', 400, response);
        }

        return response.data.options;
    }

    /**
     * Get WebAuthn conditional authentication options (for autofill passkeys)
     */
    async getWebAuthnConditionalOptions(): Promise<unknown> {
        const response = await this.request<{ options: unknown }>(
            '/api/auth/webauthn/authenticate/conditional-options',
            {
                method: 'GET',
            }
        );

        if (!response.success || !response.data?.options) {
            throw new ApiError('Failed to get conditional options', 400, response);
        }

        return response.data.options;
    }

    /**
     * Authenticate with WebAuthn
     */
    async authenticateWebAuthn(
        credentialResponse: unknown
    ): Promise<{ access_token: string; csrf_token: string }> {
        const response = await this.request<{ access_token: string; csrf_token: string }>(
            '/api/auth/webauthn/authenticate',
            {
                method: 'POST',
                body: JSON.stringify({
                    response: credentialResponse,
                }),
            }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to authenticate', 400, response);
        }

        // Update CSRF token
        if (response.data.csrf_token) {
            this.csrfToken = response.data.csrf_token;
        }

        return response.data;
    }

    /**
     * List user's WebAuthn credentials
     */
    async listWebAuthnCredentials(accessToken: string): Promise<WebAuthnCredential[]> {
        const response = await this.request<{ credentials: WebAuthnCredential[] }>(
            '/api/settings/webauthn/credentials',
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.credentials) {
            throw new ApiError('Failed to list credentials', 400, response);
        }

        return response.data.credentials;
    }

    /**
     * Delete a WebAuthn credential
     */
    async deleteWebAuthnCredential(accessToken: string, credentialId: number): Promise<void> {
        const csrfToken = await this.getCsrfToken();

        await this.request(
            `/api/settings/webauthn/credentials/${credentialId}`,
            {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
            }
        );
    }

    /**
     * Request account recovery
     */
    async requestRecovery(email: string): Promise<{ message: string }> {
        const response = await this.postWithCsrf<{ message: string }>(
            '/api/auth/recovery/request',
            { email }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Failed to request recovery', 400, response);
        }

        return response.data;
    }

    /**
     * Cancel recovery request
     */
    async cancelRecovery(cancellationToken: string): Promise<void> {
        await this.postWithCsrf(
            '/api/auth/recovery/cancel',
            { token: cancellationToken }
        );
    }

    /**
     * Verify recovery token (GET - just check if valid)
     */
    async verifyRecoveryToken(token: string): Promise<{ email: string }> {
        const response = await this.request<{ email: string }>(
            `/api/auth/recovery/confirm?token=${encodeURIComponent(token)}`,
            { method: 'GET' }
        );

        if (!response.success || !response.data) {
            throw new ApiError('Invalid or expired recovery token', 400, response);
        }

        return response.data;
    }

    /**
     * Confirm recovery (POST - actually remove credentials)
     */
    async confirmRecovery(token: string): Promise<void> {
        await this.postWithCsrf(
            '/api/auth/recovery/confirm',
            { token }
        );
    }

    /**
     * List pending recovery requests (admin only)
     */
    async listRecoveryRequests(accessToken: string): Promise<RecoveryRequest[]> {
        const response = await this.request<{ requests: RecoveryRequest[] }>(
            '/api/admin/recovery-requests',
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.requests) {
            throw new ApiError('Failed to list recovery requests', 400, response);
        }

        return response.data.requests;
    }

    /**
     * Approve recovery request (admin only)
     */
    async approveRecoveryRequest(accessToken: string, requestId: number): Promise<void> {
        const csrfToken = await this.getCsrfToken();

        await this.request(
            `/api/admin/recovery-requests/${requestId}/approve`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
            }
        );
    }

    /**
     * Reject recovery request (admin only)
     */
    async rejectRecoveryRequest(accessToken: string, requestId: number): Promise<void> {
        const csrfToken = await this.getCsrfToken();

        await this.request(
            `/api/admin/recovery-requests/${requestId}/reject`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
            }
        );
    }

    /**
     * Update an email template (admin only)
     */
    async updateEmailTemplate(accessToken: string, templateId: number, data: EmailTemplateUpdate): Promise<void> {
        const csrfToken = await this.getCsrfToken();

        await this.request(
            `/api/admin/email-templates/${templateId}`,
            {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
                body: JSON.stringify(data),
            }
        );
    }

    /**
     * Create a new locale version of an email template (admin only)
     */
    async createEmailTemplateLocale(accessToken: string, slug: string, locale: string): Promise<{ id: number }> {
        const csrfToken = await this.getCsrfToken();

        const response = await this.request<{ id: number }>(
            `/api/admin/email-templates/${slug}/locale`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
                body: JSON.stringify({ locale }),
            }
        );

        return response;
    }

    /**
     * Get all settings (admin only)
     */
    async getSettings(accessToken: string): Promise<Record<string, Record<string, {
        value: boolean | number | string;
        type: 'boolean' | 'integer' | 'string' | 'json';
        label: string | null;
        description: string;
        sensitive: boolean;
        default: boolean | number | string;
        recommended: boolean | number | string | null;
    }>>> {
        const response = await this.request<{
            settings: Record<string, Record<string, {
                value: boolean | number | string;
                type: 'boolean' | 'integer' | 'string' | 'json';
                label: string | null;
                description: string;
                sensitive: boolean;
                default: boolean | number | string;
                recommended: boolean | number | string | null;
            }>>
        }>(
            '/api/admin/settings',
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.success || !response.data?.settings) {
            throw new ApiError('Failed to get settings', 400, response);
        }

        return response.data.settings;
    }

    /**
     * Update a setting (admin only)
     */
    async updateSetting(accessToken: string, key: string, value: boolean | number | string): Promise<void> {
        const csrfToken = await this.getCsrfToken();

        await this.request(
            `/api/admin/settings/${key}`,
            {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'X-CSRF-Token': csrfToken,
                },
                body: JSON.stringify({ value }),
            }
        );
    }
}
